import * as React from 'react';
import { MessageBarType } from '@fluentui/react';
import { TripleDES } from 'crypto-js';
import { Buffer } from 'buffer';
import { useHistory } from 'react-router-dom';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import axios from 'axios';
import * as APP_CONSTANTS from '../../shared/Constants';
import { CmicContext } from '../../context/CmicContext';
import { ActionContext } from '../../context/ActionContext';
import { useValidation } from '../../hooks/useValidation';
import {
	getEnvironmentNameFromWSURL,
	getUsernameAndTenantId,
	handleDynamicEnvironmentURL,
	isInternetAvailable,
	getRecentServerURLs,
	addRecentServerURL,
} from '../../shared/UtilityFunctions';
import {
	invalidCustomStyles,
	customDropDownStyles,
	baseFontFamilyStyle,
} from '../../shared/styles';
import { handleProjectDataFiller } from '../../utility/UtilityFunctions';
import { segmentIdentifyUser, segmentTrackLogin } from '../../utility/segment/segmentUtility';
import { PREDEFINED_MSG } from '../../shared/NotificationVerbiage';
import { getOfficeAccessToken, loginToUrl } from '../../components/Auth/OAuth';
import CloudLogin from 'src/taskpane/pages/settings/CloudLogin/index';
import EnterpriseLogin from 'src/taskpane/pages/settings/EnterpriseLogin/index';
import BasicAuthLogin from 'src/taskpane/pages/settings/BasicAuthLogin/index';
import ActionRoleContainer from 'src/taskpane/pages/settings/ActionRoleContainer/index';
import Toast from 'src/taskpane/shared/Toast/index';

//Styles
const loaderStyles = {
	position: 'absolute',
	top: 0,
	left: 0,
	width: '100%',
	height: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	background: 'white',
};

let timeoutRef;

// OPTIONS

const Settings = () => {
	const { useState, useContext, useEffect } = React;

	const {
		currentOfficeTheme,
		userInfo,
		setUserInfo,
		resetCMiCData,
		setShowToast,
		setServerType,
		serverType,
		setIsCloudUser,
		isCloudUser,
		removeToast,
	} = useContext(CmicContext);

	const { resetActionData, setActionTypeData, setSelectedImagingDoc } = useContext(ActionContext);

	// Styles definition

	const textInputStyles = props => {
		return {
			fieldGroup: {
				width: '100%',
				background: currentOfficeTheme.bodyBackgroundColor,
				color: currentOfficeTheme.bodyForegroundColor,
			},
			field: {
				color: currentOfficeTheme.bodyForegroundColor,
				fontFamily: baseFontFamilyStyle,
			},

			subComponentStyles: {
				label: getLabelStyle,
			},
			errorMessage: {
				padding: 0,
			},
		};
	};

	const getLabelStyle = props => {
		return {
			root: {
				color: currentOfficeTheme.bodyForegroundColor,
				':after': {
					content: '',
					color: currentOfficeTheme.bodyForegroundColor,
				},
			},
		};
	};

	const {
		val: usernameVal,
		isValid: isUsernameValid,
		isFocused: isUsernameFocused,
		handleReset: resetUsername,
		error: usernameError,
		label: usernameLabel,
		handleValueChange: handleUsernameChange,
		handleOnBlur: handleOnUsernameBlur,
	} = useValidation({
		isRequired: true,
		minLength: 2,
		label: 'Username',
		val: userInfo.tenantID ? userInfo.tenantID.split('||')[1] : userInfo.username,
	});

	const {
		val: passwordVal,
		isValid: isPasswordValid,
		isFocused: isPasswordFocused,
		handleReset: resetPassword,
		error: passwordError,
		label: passwordLabel,
		handleValueChange: handlePasswordChange,
		handleOnBlur: handleOnPasswordBlur,
	} = useValidation({ isRequired: true, minLength: 6, label: 'Password', val: userInfo.password });

	const defaultURLVal = getRecentServerURLs()[0] || '';

	const {
		val: URLVal,
		isValid: isURLValid,
		isFocused: isURLFocused,
		handleReset: resetURL,
		error: URLError,
		label: URLLabel,
		handleValueChange: handleURLValueChange,
		handleOnBlur: handleOnURLBlur,
	} = useValidation({
		isRequired: true,
		minLength: 2,
		label: 'Environment URL',
		val: defaultURLVal,
	});

	const [actionChoice, setActionChoice] = useState(userInfo.actionRole);

	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [isCapsLockOn, setIsCapsLockOn] = useState(false);
	const [isEnterpriseOAuth, setIsEnterpriseOAuth] = useState(true);

	const navigate = useHistory();

	useEffect(async () => {
		const childResponse = event => {
			if (event?.data) {
				// console.log(event)
			}
		};
		window.addEventListener('message', childResponse);
	}, []);

	const handleDropValueChange = (data, event) => {
		setServerType({
			data,
			err: '',
		});
	};

	const handleRoleChange = () => {
		if (isLoading) return;
		setIsCloudUser(prev => !prev);
		resetData();
	};

	const handleActionRoleChange = event => {
		setActionChoice(event.target.value);
	};

	const handleGoToBasicAuth = () => {
		setIsEnterpriseOAuth(false);
		if (URLError) resetURL();
		if (usernameError) resetUsername();
		if (passwordError) resetPassword();
	};

	const handleEnterpriseSubmit = event => {
		event?.preventDefault();

		if (!isInternetAvailable(setShowToast)) {
			return;
		}

		handleOnFocus();
		const validateEnterpriseURL = handleOnURLBlur();
		const validateUsername = handleOnUsernameBlur();
		const validatePass = handleOnPasswordBlur();
		if (validateUsername && validatePass && validateEnterpriseURL) {
			handleSignIn();
		}
	};

	const handleSignInError = error => {
		if (!window.navigator.onLine && error.code === 'ERR_NETWORK') {
			setShowToast({
				isVisible: true,
				msg: 'No internet',
				type: MessageBarType.error,
			});
		} else {
			if (error?.response?.status === 403) {
				setShowToast({
					isVisible: true,
					msg: error?.response?.data?.message || PREDEFINED_MSG.IS_VALID_ECM_USER,
					type: MessageBarType.error,
				});
				// todo error for office token
				// } else if (error?.message) {
				//   setShowToast({
				//     isVisible: true,
				//     msg: "Unable to obtain office access token",
				//     type: MessageBarType.error,
				//   });
			} else {
				setShowToast({
					isVisible: true,
					msg: 'Unable to Sign In' || 'Something went wrong!',
					type: MessageBarType.error,
				});
			}

			clearTimeout(timeoutRef);

			timeoutRef = removeToast(5000);
		}
		setIsLoading(false);
	};

	// OAuth 2.0 Entrprise user sign in
	const handleEnterpriseOAuthSignIn = async event => {
		event?.preventDefault();
		if (!isInternetAvailable(setShowToast)) {
			return;
		}
		handleOnFocus();
		const validateEnterpriseURL = handleOnURLBlur();

		if (validateEnterpriseURL) {
			setIsLoading(true);

			const officeAccessTokenResponse = await getOfficeAccessToken();
			if (!officeAccessTokenResponse.success) {
				handleSignInError();
				setIsLoading(false);
				return;
			}

			localStorage.removeItem(APP_CONSTANTS.CMIC_KEYS.SELCTED_IMAGING_DOC_ENCRYPTION_KEY);
			resetCacheData();

			let officeAccessToken = officeAccessTokenResponse.data;
			let enterpriseApiLoginUrl = `${URLVal}${APP_CONSTANTS.CMIC_FIELD_REST__NAMES.LOGIN}`;
			if (actionChoice === APP_CONSTANTS.ACTION_ROLE.ECM)
				enterpriseApiLoginUrl = `${URLVal}${APP_CONSTANTS.CMIC_FIELD_REST__NAMES.LOGIN_ECM}`;

			try {
				const enterpriseResponse = await loginToUrl({ officeAccessToken, loginUrl: enterpriseApiLoginUrl });
				if (typeof enterpriseResponse.data !== 'object') {
					setIsLoading(false);
					setShowToast({
						isVisible: true,
						msg: 'Something went wrong!',
						type: MessageBarType.error,
					});
				} else {
					// set default project
					let projectListData;
					if (enterpriseResponse.data.defaultProjectUuid) {
						const projectData = await setDefaultProjectData(enterpriseResponse.data, officeAccessToken);
						projectListData = handleProjectDataFiller(
							{
								value: projectData?.PmpProjOraseq,
								nodeId: projectData?.PmpProjOraseq,
								label: `${projectData?.PmpProjName} (${projectData?.PmpProjCode})`,
								timeStamp: new Date(),
								...projectData,
							},
							true,
						);
					}

					saveInfo({ ...enterpriseResponse.data, accessToken: officeAccessToken });
					addRecentServerURL(URLVal);

					if (actionChoice === APP_CONSTANTS.ACTION_ROLE.ECM) {
						setSelectedImagingDoc({ value: '', label: '' });
						resetActionData();
					}
					setIsLoading(false);
					navigate.replace('/home');
				}
			} catch (error) {
				console.log({ error });
				handleSignInError(error);
			}
		}
	};

	const handleCloudSubmit = async e => {
		e.preventDefault();
		if (!isInternetAvailable(setShowToast)) {
			return;
		}
		setIsSubmitted(true);

		if (!serverType.data.url) {
			setServerType({
				data: { url: '', label: '' },
				err: "Please select Client's location",
			});
		} else {
			setIsLoading(true);
			handleOnFocus();

			const officeAccessTokenResponse = await getOfficeAccessToken();
			if (!officeAccessTokenResponse.success) {
				handleSignInError();
				setIsLoading(false);
				return;
			}

			localStorage.removeItem(APP_CONSTANTS.CMIC_KEYS.SELCTED_IMAGING_DOC_ENCRYPTION_KEY);
			resetCacheData();

			let officeAccessToken = officeAccessTokenResponse.data;
			let cloudServerApiUrl = `${serverType?.data?.url}${APP_CONSTANTS.CMIC_FIELD_REST__NAMES.LOGIN}`;
			if (actionChoice === APP_CONSTANTS.ACTION_ROLE.ECM)
				cloudServerApiUrl = `${serverType?.data?.url}${APP_CONSTANTS.CMIC_FIELD_REST__NAMES.LOGIN_ECM}`;

			try {
				const cloudResponse = await loginToUrl({ officeAccessToken, loginUrl: cloudServerApiUrl });
				if (typeof cloudResponse.data !== 'object') {
					setIsLoading(false);
					setShowToast({
						isVisible: true,
						msg: 'Something went wrong!',
						type: MessageBarType.error,
					});
				} else {
					// set default project
					let projectListData;
					if (cloudResponse.data.defaultProjectUuid) {
						const projectData = await setDefaultProjectData(cloudResponse.data, officeAccessToken);
						projectListData = handleProjectDataFiller(
							{
								value: projectData?.PmpProjOraseq,
								nodeId: projectData?.PmpProjOraseq,
								label: `${projectData?.PmpProjName} (${projectData?.PmpProjCode})`,
								timeStamp: new Date(),
								...projectData,
							},
							true,
						);
					}

					saveInfo({ ...cloudResponse.data, accessToken: officeAccessToken });
					if (isCloudUser) {
						segmentIdentifyUser(cloudResponse.data, {
							tenantID: serverType?.data?.tenantId,
							isCloudUser,
						});
						segmentTrackLogin();
					}

					if (actionChoice === APP_CONSTANTS.ACTION_ROLE.ECM) {
						setSelectedImagingDoc({ value: '', label: '' });
						resetActionData();
					}
					setIsLoading(false);
					navigate.replace('/home');
				}
			} catch (error) {
				console.log({ error });
				handleSignInError(error);
			}
		}
	};

	const setDefaultProjectData = async (data, accessToken) => {
		return new Promise(async (resolve, reject) => {
			const URL = `${serverType.data.url || URLVal}${APP_CONSTANTS.CMIC_FIELD_REST__NAMES.projects}/${data.defaultProjectUuid
				}?onlyData=true`;
			try {
				let authObj =
					isCloudUser || isEnterpriseOAuth
						? {
							headers: {
								Authorization: `Bearer ${accessToken}`,
							},
						}
						: {
							auth: {
								username: usernameVal,
								password: passwordVal,
							},
						};
				const result = await axios.get(URL, {
					...authObj,
				});

				if (typeof result.data === 'object') {
					resolve(result.data);
				} else {
					setShowToast({
						isVisible: true,
						msg: 'Something went wrong!',
						type: MessageBarType.error,
					});
					setIsLoading(false);
				}
			} catch (error) {
				setShowToast({
					isVisible: true,
					msg: error.message || 'Something went wrong!',
					type: MessageBarType.error,
				});
				setIsLoading(false);
			}
		});
	};

	const handleSignIn = async () => {
		const newURL = serverType.data?.url || URLVal;
		if (userInfo.username !== usernameVal || userInfo.serverURL !== newURL) {
			localStorage.removeItem(APP_CONSTANTS.CMIC_KEYS.SELCTED_IMAGING_DOC_ENCRYPTION_KEY);
			resetCacheData();
		}
		setIsLoading(true);

		try {
			let URL;

			if (actionChoice === APP_CONSTANTS.ACTION_ROLE.ECM)
				URL = `${newURL}${APP_CONSTANTS.CMIC_FIELD_REST__NAMES.LOGIN_ECM}`;
			else URL = `${newURL}${APP_CONSTANTS.CMIC_FIELD_REST__NAMES.LOGIN}`;

			const auth = {
				username: usernameVal,
				password: passwordVal,
			};

			const result = await axios.get(URL, {
				auth,
			});

			let projectListData;
			if (result.data.defaultProjectUuid) {
				const projectData = await setDefaultProjectData(result.data);
				projectListData = handleProjectDataFiller(
					{
						value: projectData?.PmpProjOraseq,
						nodeId: projectData?.PmpProjOraseq,
						label: `${projectData?.PmpProjName} (${projectData?.PmpProjCode})`,
						timeStamp: new Date(),
						...projectData,
					},
					true,
				);
			}

			saveInfo(result.data);
			addRecentServerURL(newURL);

			if (actionChoice === APP_CONSTANTS.ACTION_ROLE.ECM) {
				if (typeof result.data === 'object') {
					setSelectedImagingDoc({ value: '', label: '' });
					resetActionData();
				} else {
					setShowToast({
						isVisible: true,
						msg: 'Something went wrong!',
						type: MessageBarType.error,
					});
				}
			} else {
				if (typeof result.data !== 'object') {
					setShowToast({
						isVisible: true,
						msg: 'Something went wrong!',
						type: MessageBarType.error,
					});
				}
			}

			setIsLoading(false);
			navigate.replace('/home');
		} catch (error) {
			handleSignInError(error);
		}
	};

	const resetData = () => {
		resetUsername();
		resetPassword();
		setServerType({
			data: { key: '', label: '' },
			err: '',
		});
		setIsSubmitted(false);
	};

	const resetCacheData = () => {
		resetCMiCData();
		resetActionData();
		setActionTypeData({
			nodeType: null,
			label: null,
			type: null,
		});
		localStorage.removeItem(APP_CONSTANTS.CMIC_KEYS.PROJECT_LIST_ENCRYPTION_KEY);
		localStorage.removeItem(APP_CONSTANTS.CMIC_KEYS.SELCTED_PROJ_ENCRYPTION_KEY);
	};

	const saveInfo = (userData = null) => {
		const requiredInfo = {
			tenantID: isCloudUser ? `${serverType.data?.tenantId}` : '',
			username: isCloudUser ? `${serverType.data?.clientId}` : usernameVal,
			password: passwordVal,
			serverURL: serverType.data.url || URLVal,
			serverType: isCloudUser ? serverType.data : { url: '', label: '' },
			isTreeShown: 0,
			actionRole: actionChoice,
			usernameAndTenantId: getUsernameAndTenantId(
				serverType.data?.tenantId,
				userData?.pmUser?.toLowerCase() || usernameVal,
			),
			environmentName: getEnvironmentNameFromWSURL(serverType.data.url || URLVal),
			info: userData || {},
			accessToken: userData?.accessToken,
			isCloudUser: isCloudUser,
			isEnterpriseOAuth: isEnterpriseOAuth,
		};

		let ciphertext = TripleDES.encrypt(
			JSON.stringify(requiredInfo),
			APP_CONSTANTS.CMIC_KEYS.SERVER_SETTINGS_ENCRYPTION_KEY,
		).toString();

		const encodedString = Buffer.from(ciphertext).toString('base64');

		// setting userInfo after submitting data
		setUserInfo({
			...requiredInfo,
		});

		localStorage.setItem(APP_CONSTANTS.CMIC_KEYS.SERVER_SETTINGS_ENCRYPTION_KEY, encodedString); // SAVING ENCRYPTED KEY
	};

	const ActionRoleContainerCallback = React.useCallback(
		() => <ActionRoleContainer actionChoice={actionChoice} handleActionRoleChange={handleActionRoleChange} />,
		[actionChoice],
	);

	const handleOnFocus = () => {
		setShowToast(APP_CONSTANTS.INIT_TOAST_CONFIG);
		clearTimeout(timeoutRef);
	};

	const renderCloudLogin = () => {
		return <CloudLogin
			handleRoleChange={handleRoleChange}
			handleCloudSubmit={handleCloudSubmit}
			handleDropValueChange={handleDropValueChange}
			handleOnFocus={handleOnFocus}
			isSubmitted={isSubmitted}
			invalidCustomStyles={invalidCustomStyles}
			customDropDownStyles={customDropDownStyles}
			handleDynamicEnvironmentURL={handleDynamicEnvironmentURL}
			ActionRoleContainer={ActionRoleContainerCallback}
		/>
	}

	const renderEnterpriseLogin = () => {
		return <EnterpriseLogin
			handleRoleChange={handleRoleChange}
			handleEnterpriseUrlSubmit={handleEnterpriseOAuthSignIn}
			handleOnFocus={handleOnFocus}
			ActionRoleContainer={ActionRoleContainerCallback}
			URLLabel={URLLabel}
			URLVal={URLVal}
			URLError={URLError}
			isURLValid={isURLValid}
			handleURLValueChange={handleURLValueChange}
			handleOnURLBlur={handleOnURLBlur}
			textInputStyles={textInputStyles}
			handleGoToBasicAuth={handleGoToBasicAuth}
		/>
	};

	const renderBasicAuthLogin = () => {
		return <BasicAuthLogin
			handleBack={() => { setIsEnterpriseOAuth(true); }}
			handleOnFocus={handleOnFocus}
			ActionRoleContainer={ActionRoleContainerCallback}
			handleBasicAuthLoginSubmit={handleEnterpriseSubmit}
			URLLabel={URLLabel}
			URLVal={URLVal}
			handleURLValueChange={handleURLValueChange}
			handleOnURLBlur={handleOnURLBlur}
			URLError={URLError}
			isURLValid={isURLValid}
			textInputStyles={textInputStyles}
			usernameLabel={usernameLabel}
			usernameVal={usernameVal}
			handleUsernameChange={handleUsernameChange}
			handleOnUsernameBlur={handleOnUsernameBlur}
			usernameError={usernameError}
			isUsernameValid={isUsernameValid}
			passwordLabel={passwordLabel}
			passwordVal={passwordVal}
			handlePasswordChange={handlePasswordChange}
			handleOnPasswordBlur={handleOnPasswordBlur}
			passwordError={passwordError}
			isCapsLockOn={isCapsLockOn}
			isPasswordValid={isPasswordValid}
			setIsCapsLockOn={setIsCapsLockOn}
			actionChoice={actionChoice}
		/>
	};

	const renderAuthPage = () => {
		if (isCloudUser) {
			return renderCloudLogin();
		} else if (isEnterpriseOAuth) {
			return renderEnterpriseLogin();
		} else return renderBasicAuthLogin();
	};

	const renderLoading = () => (
		<div style={loaderStyles}>
			<Spinner size={SpinnerSize.large} label="Signing In..." />
		</div>
	);

	return (
		<div>
			<Toast />
			{renderAuthPage()}
			{isLoading && renderLoading()}
		</div>
	);
};

export default Settings;
